import { CompaniesResponse, ApiError, SearchRequest, Company, FilterCriteria } from '../types/company';
import { PeopleResponse, SearchRequest as PeopleSearchRequest, Person } from '../types/people';
import { Campaign, LeadFinderStatsResponse } from '../types/outreach';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
const OUTREACH_API_BASE_URL = process.env.REACT_APP_OUTREACH_API_BASE_URL || 'http://localhost:3002/api/outreach';

// Helper to decode JWT token (without verification)
const decodeToken = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

// Helper to check if token is expired or expiring soon (within 2 minutes)
const isTokenExpiringSoon = (token: string | null): boolean => {
  if (!token) return true;
  
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  
  const expirationTime = decoded.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const twoMinutes = 2 * 60 * 1000; // 2 minutes in milliseconds
  
  return expirationTime - currentTime < twoMinutes;
};

// Helper to refresh access token
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

const refreshToken = async (): Promise<boolean> => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
      const refreshTokenValue = localStorage.getItem('ems_refresh_token');
      
      if (!refreshTokenValue) {
        return false;
      }

      // Use direct fetch for refresh endpoint (no auth header needed, refresh token is in body)
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: refreshTokenValue }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Refresh token is invalid or expired
        localStorage.removeItem('ems_access_token');
        localStorage.removeItem('ems_refresh_token');
        localStorage.removeItem('ems_user');
        return false;
      }

      // Update tokens
      localStorage.setItem('ems_access_token', data.accessToken);
      localStorage.setItem('ems_refresh_token', data.refreshToken);
      
      return true;
    } catch (error) {
      console.error('Error refreshing token:', error);
      localStorage.removeItem('ems_access_token');
      localStorage.removeItem('ems_refresh_token');
      localStorage.removeItem('ems_user');
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Helper to get auth token from localStorage
const getAuthToken = (): string | null => {
  const token = localStorage.getItem('ems_access_token');
  // Debug: Log if token is missing (but not for every call to avoid spam)
  if (!token && typeof window !== 'undefined' && window.location.pathname !== '/login') {
    console.warn('No access token found in localStorage');
  }
  return token;
};


// Wrapper for fetch that handles token refresh automatically
const fetchWithAuth = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  // Skip refresh logic for refresh endpoint, login, and verify-2fa to avoid circular dependency
  const isRefreshEndpoint = url.includes('/auth/refresh');
  const isLoginEndpoint = url.includes('/auth/login');
  const isVerify2FAEndpoint = url.includes('/auth/verify-2fa');
  const shouldSkipRefresh = isRefreshEndpoint || isLoginEndpoint || isVerify2FAEndpoint;
  
  // Get current token
  let token = getAuthToken();
  
  // PROACTIVE REFRESH: Check if token is expiring soon BEFORE making the request
  // Only check if we have a token and it's not a special endpoint
  if (token && !shouldSkipRefresh) {
    // Check if refresh is already in progress - if so, wait for it
    if (isRefreshing && refreshPromise) {
      // Wait for ongoing refresh to complete
      const refreshed = await refreshPromise;
      if (refreshed) {
        // Get the new token after refresh completes
        token = getAuthToken();
      }
    } else if (isTokenExpiringSoon(token)) {
      // Token is expiring soon or already expired, refresh proactively
      // IMPORTANT: Wait for refresh to complete before proceeding
      const refreshed = await refreshToken();
      if (refreshed) {
        // Get the new token after refresh - always get fresh token from storage
        token = getAuthToken();
      }
      // If refresh failed, continue with existing token (reactive refresh will handle it)
    }
  }
  
  // Prepare headers
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
  };
  
  // Only set Content-Type if not already provided (don't override FormData)
  // FormData needs the browser to set Content-Type with boundary automatically
  if (!headers['Content-Type'] && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  
  // Only add Authorization header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else if (!shouldSkipRefresh) {
    // Log warning for missing token on protected endpoints
    console.warn(`API call to ${url} made without access token`);
  }
  
  // Make the request
  const response = await fetch(url, {
    ...options,
    headers,
  });

  // REACTIVE REFRESH: Fallback if proactive refresh didn't work or token expired during request
  // If 401, try to refresh and retry once (skip for special endpoints)
  if (response.status === 401 && !isRefreshing && !shouldSkipRefresh) {
    const refreshed = await refreshToken();
    if (refreshed) {
      // Retry with new token
      const newToken = getAuthToken();
      const newHeaders: Record<string, string> = {
        ...(options.headers as Record<string, string> || {}),
      };
      
      // Only set Content-Type if not already provided (don't override FormData)
      if (!newHeaders['Content-Type'] && !(options.body instanceof FormData)) {
        newHeaders['Content-Type'] = 'application/json';
      }
      
      if (newToken) {
        newHeaders['Authorization'] = `Bearer ${newToken}`;
      }
      
      return fetch(url, {
        ...options,
        headers: newHeaders,
      });
    }
  }

  return response;
};

export class ApiService {
  static API_BASE_URL = API_BASE_URL;
  static OUTREACH_API_BASE_URL = OUTREACH_API_BASE_URL;

  private static async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      // Handle 401 Unauthorized - token expired or invalid (refresh already attempted in fetchWithAuth)
      if (response.status === 401) {
        // Clone response to read body without consuming it
        const responseClone = response.clone();
        let errorMessage = 'Unauthorized';
        
        try {
          const errorData = await responseClone.json();
          errorMessage = errorData.message || errorData.error || 'Unauthorized';
        } catch {
          // If response is not JSON, use default message
        }
        
        // Check if we have a refresh token
        const refreshToken = localStorage.getItem('ems_refresh_token');
        
        // Only clear tokens and redirect if:
        // 1. No refresh token exists (session truly expired)
        // 2. OR we're on a protected route and refresh already failed
        if (!refreshToken) {
          // No refresh token available, clear everything and redirect
          localStorage.removeItem('ems_access_token');
          localStorage.removeItem('ems_refresh_token');
          localStorage.removeItem('ems_user');
          // Only redirect if not already on login page
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          throw new Error('Session expired. Please click refresh to continue.');
        }
        
        // If we have a refresh token but still got 401, refresh must have failed
        // Don't redirect immediately - let the error propagate
        // Update error message to include refresh instruction
        const refreshMessage = errorMessage.includes('expired') || errorMessage.includes('Invalid') || errorMessage.includes('Unauthorized')
          ? `${errorMessage}. Please click refresh to continue.`
          : errorMessage;
        throw new Error(refreshMessage);
      }
      
      // Try to parse error response
      let errorMessage = 'API request failed';
      try {
        const error: ApiError = await response.json();
        errorMessage = error.message || error.error || errorMessage;
      } catch {
        // If response is not JSON, use status text
        errorMessage = response.statusText || errorMessage;
      }
      throw new Error(errorMessage);
    }
    return response.json();
  }

  static async getCompanies(params?: {
    cursor?: string | null;
    page?: string | number | null;
    limit?: number;
    sort?: string;
    direction?: 'ASC' | 'DESC';
    refresh?: boolean;
  }): Promise<CompaniesResponse> {
    const searchParams = new URLSearchParams();
    
    if (params?.cursor) searchParams.append('cursor', params.cursor);
    if (params?.page !== null && params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.sort) searchParams.append('sort', params.sort);
    if (params?.direction) searchParams.append('direction', params.direction);
    if (params?.refresh) searchParams.append('refresh', 'true');

    const url = `${API_BASE_URL}/companies${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    const response = await fetchWithAuth(url);
    return this.handleResponse<CompaniesResponse>(response);
  }

  static async searchCompanies(searchRequest: SearchRequest & { refresh?: boolean }): Promise<CompaniesResponse> {
    const response = await fetchWithAuth(`${API_BASE_URL}/companies/search`, {
      method: 'POST',
      body: JSON.stringify(searchRequest),
    });
    return this.handleResponse<CompaniesResponse>(response);
  }

  static async exportCompanies(filters: FilterCriteria[] = []): Promise<Blob> {
    const searchParams = new URLSearchParams();
    if (filters.length > 0) {
      searchParams.append('filters', JSON.stringify(filters));
    }

    const url = `${API_BASE_URL}/companies/export${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    const response = await fetchWithAuth(url);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Export failed', message: 'Unknown error' }));
      throw new Error(error.message || 'Export failed');
    }
    
    return response.blob();
  }

  static async exportPeople(filters: FilterCriteria[] = []): Promise<Blob> {
    const searchParams = new URLSearchParams();
    if (filters.length > 0) {
      searchParams.append('filters', JSON.stringify(filters));
    }

    const url = `${API_BASE_URL}/people/export${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    const response = await fetchWithAuth(url);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Export failed', message: 'Unknown error' }));
      throw new Error(error.message || 'Export failed');
    }
    
    return response.blob();
  }

  static async getCompanyIndustryTags(): Promise<{ industryTags: string[] }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/companies/industry-tags`);
    return this.handleResponse<{ industryTags: string[] }>(response);
  }

  static async getVerificationStatuses(): Promise<{ verificationStatuses: string[] }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/people/verification-status`);
    if (!response.ok) {
      throw new Error('Failed to fetch verification statuses');
    }
    return response.json();
  }

  static async getPeopleIndustryTags(): Promise<{ industryTags: string[] }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/people/industry-tags`);
    return this.handleResponse<{ industryTags: string[] }>(response);
  }

  static async getCompanyIndustryTagTwoOptions(): Promise<{ industryTagTwoOptions: string[] }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/companies/industry-tag-two-options`);
    return this.handleResponse<{ industryTagTwoOptions: string[] }>(response);
  }

  static async getCompanyIndustryTagThreeOptions(): Promise<{ industryTagThreeOptions: string[] }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/companies/industry-tag-three-options`);
    return this.handleResponse<{ industryTagThreeOptions: string[] }>(response);
  }

  static async getCompanyIndustryTagFourOptions(): Promise<{ industryTagFourOptions: string[] }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/companies/industry-tag-four-options`);
    return this.handleResponse<{ industryTagFourOptions: string[] }>(response);
  }

  // Company CRUD API methods
  static async createCompany(data: {
    name: string;
    description?: string | null;
    website?: string | null;
    location?: string | null;
    employees_onlinkedin?: number | null;
    state_usa?: number | null;
    type_?: string | null;
    license?: string | null;
    country?: number | null;
    industry_tag?: number | null;
    status?: string | null;
    note?: string | null;
    Facebook?: string | null;
    twitter?: string | null;
    ig?: string | null;
    Linkedin?: string | null;
    Youtube?: string | null;
    company_address?: string | null;
    industry_tag_two?: number | null;
    industry_tag_three?: number | null;
    industry_tag_four?: number | null;
    snov_id?: string | null;
    deliverable?: number | null;
    risky?: number | null;
    unknown?: number | null;
    undeliverable?: number | null;
    not_verified?: number | null;
    in_mailer?: number | null;
    unsubscribe?: number | null;
    bounce?: number | null;
    phone?: string | null;
    linkedin_id?: string | null;
    clean_name?: string | null;
    suspicious?: number | null;
  }): Promise<Company> {
    const response = await fetchWithAuth(`${API_BASE_URL}/companies`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return this.handleResponse<Company>(response);
  }

  static async updateCompany(id: number, data: {
    name: string;
    description?: string | null;
    website?: string | null;
    location?: string | null;
    employees_onlinkedin?: number | null;
    state_usa?: number | null;
    type_?: string | null;
    license?: string | null;
    country?: number | null;
    industry_tag?: number | null;
    status?: string | null;
    note?: string | null;
    Facebook?: string | null;
    twitter?: string | null;
    ig?: string | null;
    Linkedin?: string | null;
    Youtube?: string | null;
    company_address?: string | null;
    industry_tag_two?: number | null;
    industry_tag_three?: number | null;
    industry_tag_four?: number | null;
    snov_id?: string | null;
    deliverable?: number | null;
    risky?: number | null;
    unknown?: number | null;
    undeliverable?: number | null;
    not_verified?: number | null;
    in_mailer?: number | null;
    unsubscribe?: number | null;
    bounce?: number | null;
    phone?: string | null;
    linkedin_id?: string | null;
    clean_name?: string | null;
    suspicious?: number | null;
  }): Promise<Company> {
    const response = await fetchWithAuth(`${API_BASE_URL}/companies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return this.handleResponse<Company>(response);
  }

  static async deleteCompany(id: number): Promise<{ message: string }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/companies/${id}`, {
      method: 'DELETE',
    });
    return this.handleResponse<{ message: string }>(response);
  }

  static async getPTag1Options(): Promise<{ pTag1Options: string[] }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/people/p-tag-1-options`, {
    });
    return this.handleResponse<{ pTag1Options: string[] }>(response);
  }

  static async getPTag2Options(): Promise<{ pTag2Options: string[] }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/people/p-tag-2-options`);
    return this.handleResponse<{ pTag2Options: string[] }>(response);
  }

  static async getPTag3Options(): Promise<{ pTag3Options: string[] }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/people/p-tag-3-options`, {
    });
    return this.handleResponse<{ pTag3Options: string[] }>(response);
  }

  static async getPTag4Options(): Promise<{ pTag4Options: string[] }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/people/p-tag-4-options`, {
    });
    return this.handleResponse<{ pTag4Options: string[] }>(response);
  }

  static async getIndustryTagTwoOptions(): Promise<{ industryTagTwoOptions: string[] }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/people/industry-tag-two-options`, {
    });
    return this.handleResponse<{ industryTagTwoOptions: string[] }>(response);
  }

  static async getIndustryTagThreeOptions(): Promise<{ industryTagThreeOptions: string[] }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/people/industry-tag-three-options`, {
    });
    return this.handleResponse<{ industryTagThreeOptions: string[] }>(response);
  }

  static async getIndustryTagFourOptions(): Promise<{ industryTagFourOptions: string[] }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/people/industry-tag-four-options`, {
    });
    return this.handleResponse<{ industryTagFourOptions: string[] }>(response);
  }

  // People API methods
  static async getPeople(params?: {
    cursor?: number | null;
    page?: number | null;
    limit?: number;
    sort?: string;
    direction?: 'ASC' | 'DESC';
    refresh?: boolean;
  }): Promise<PeopleResponse> {
    const searchParams = new URLSearchParams();
    
    if (params?.cursor) {
      searchParams.append('cursor', params.cursor.toString());
    }
    if (params?.page !== null && params?.page !== undefined) {
      searchParams.append('page', params.page.toString());
    }
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString());
    }
    if (params?.sort) {
      searchParams.append('sort', params.sort);
    }
    if (params?.direction) {
      searchParams.append('direction', params.direction);
    }
    if (params?.refresh) {
      searchParams.append('refresh', 'true');
    }

    const url = `${API_BASE_URL}/people${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await fetchWithAuth(url);
    return this.handleResponse<PeopleResponse>(response);
  }

  static async searchPeople(request: PeopleSearchRequest & { refresh?: boolean }): Promise<PeopleResponse> {
    const response = await fetchWithAuth(`${API_BASE_URL}/people/search`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return this.handleResponse<PeopleResponse>(response);
  }

  // People CRUD API methods
  static async createPerson(data: Partial<Person>): Promise<Person> {
    const response = await fetchWithAuth(`${API_BASE_URL}/people`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return this.handleResponse<Person>(response);
  }

  static async updatePerson(id: number, data: Partial<Person>): Promise<Person> {
    const response = await fetchWithAuth(`${API_BASE_URL}/people/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return this.handleResponse<Person>(response);
  }

  static async deletePerson(id: number): Promise<{ message: string }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/people/${id}`, {
      method: 'DELETE',
    });
    return this.handleResponse<{ message: string }>(response);
  }

  // Industry Tags CRUD API methods
  static async getIndustryTags(
    cursor?: number | null,
    limit?: number,
    sort?: string,
    direction?: 'ASC' | 'DESC',
    refresh?: boolean
  ): Promise<any> {
    const searchParams = new URLSearchParams();
    
    if (cursor) searchParams.append('cursor', cursor.toString());
    if (limit) searchParams.append('limit', limit.toString());
    if (sort) searchParams.append('sort', sort);
    if (direction) searchParams.append('direction', direction);
    if (refresh) searchParams.append('refresh', 'true');

    const url = `${API_BASE_URL}/industry-tags?${searchParams.toString()}`;
    const response = await fetchWithAuth(url);
    return this.handleResponse<any>(response);
  }

  static async searchIndustryTags(request: any & { refresh?: boolean }): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/industry-tags/search`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return this.handleResponse<any>(response);
  }

  static async getIndustryTag(id: number): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/industry-tags/${id}`, {
    });
    return this.handleResponse<any>(response);
  }

  static async createIndustryTag(data: {
    name: string;
    status?: number;
    type_?: number | null;
    observations?: string | null;
    newsletter?: string | null;
  }): Promise<any> {
    // CRITICAL: Build payload explicitly to ensure 0 values are included
    // Don't rely on object spread which might omit falsy values
    const payload: Record<string, any> = {
      name: data.name,
    };
    
    // Explicitly include status if it's defined (including 0)
    if (data.status !== undefined) {
      payload.status = data.status;
    }
    
    // Explicitly include type_ if it's defined (including 0)
    if (data.type_ !== undefined) {
      payload.type_ = data.type_;
    }
    
    // Explicitly include observations if it's defined
    if (data.observations !== undefined) {
      payload.observations = data.observations;
    }

    // Explicitly include newsletter if it's defined
    if (data.newsletter !== undefined) {
      payload.newsletter = data.newsletter;
    }
    
    const jsonBody = JSON.stringify(payload);
    
    const response = await fetchWithAuth(`${API_BASE_URL}/industry-tags`, {
      method: 'POST',
      body: jsonBody,
    });
    return this.handleResponse<any>(response);
  }

  static async updateIndustryTag(id: number, data: {
    name: string;
    status: number;
    type_?: number | null;
    observations?: string | null;
    newsletter?: string | null;
  }): Promise<any> {
    // CRITICAL: Ensure 0 values are explicitly included in the payload
    // Build a clean object that explicitly includes all fields, even if they're 0
    const payload: {
      name: string;
      status: number;
      type_?: number | null;
      observations?: string | null;
      newsletter?: string | null;
    } = {
      name: data.name,
      status: data.status,
    };
    
    // Explicitly include type_ if it exists (even if 0)
    if ('type_' in data) {
      payload.type_ = data.type_;
    }
    
    // Explicitly include observations if it exists
    if ('observations' in data) {
      payload.observations = data.observations;
    }

    // Explicitly include newsletter if it exists
    if ('newsletter' in data) {
      payload.newsletter = data.newsletter;
    }
    
    const jsonBody = JSON.stringify(payload);
    
    const response = await fetchWithAuth(`${API_BASE_URL}/industry-tags/${id}`, {
      method: 'PUT',
      body: jsonBody,
    });
    return this.handleResponse<any>(response);
  }

  static async deleteIndustryTag(id: number): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/industry-tags/${id}`, {
      method: 'DELETE',
    });
    return this.handleResponse<any>(response);
  }

  // Countries CRUD API methods
  static async getCountries(
    cursor?: number | null,
    limit?: number,
    sort?: string,
    direction?: 'ASC' | 'DESC',
    refresh?: boolean
  ): Promise<any> {
    const searchParams = new URLSearchParams();
    
    if (cursor) {
      searchParams.append('cursor', cursor.toString());
    }
    if (limit) {
      searchParams.append('limit', limit.toString());
    }
    if (sort) {
      searchParams.append('sort', sort);
    }
    if (direction) {
      searchParams.append('direction', direction);
    }
    if (refresh) {
      searchParams.append('refresh', 'true');
    }

    const url = `${API_BASE_URL}/countries${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await fetchWithAuth(url);
    return this.handleResponse<any>(response);
  }

  static async searchCountries(request: {
    filters: any[];
    cursor?: number | null;
    limit?: number;
    sort?: string;
    direction?: 'ASC' | 'DESC';
    refresh?: boolean;
  }): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/countries/search`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return this.handleResponse<any>(response);
  }

  static async getCountry(id: number): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/countries/${id}`, {
    });
    return this.handleResponse<any>(response);
  }

  static async createCountry(data: {
    name: string;
    abbreviation?: string | null;
  }): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/countries`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return this.handleResponse<any>(response);
  }

  static async updateCountry(id: number, data: {
    name: string;
    abbreviation?: string | null;
  }): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/countries/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return this.handleResponse<any>(response);
  }

  static async deleteCountry(id: number): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/countries/${id}`, {
      method: 'DELETE',
    });
    return this.handleResponse<any>(response);
  }

  // States CRUD API methods
  static async getStates(
    cursor?: number | null,
    limit?: number,
    sort?: string,
    direction?: 'ASC' | 'DESC',
    refresh?: boolean
  ): Promise<any> {
    const searchParams = new URLSearchParams();
    
    if (cursor) {
      searchParams.append('cursor', cursor.toString());
    }
    if (limit) {
      searchParams.append('limit', limit.toString());
    }
    if (sort) {
      searchParams.append('sort', sort);
    }
    if (direction) {
      searchParams.append('direction', direction);
    }
    if (refresh) {
      searchParams.append('refresh', 'true');
    }

    const url = `${API_BASE_URL}/states${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await fetchWithAuth(url);
    return this.handleResponse<any>(response);
  }

  static async searchStates(request: {
    filters: any[];
    cursor?: number | null;
    limit?: number;
    sort?: string;
    direction?: 'ASC' | 'DESC';
    refresh?: boolean;
  }): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/states/search`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return this.handleResponse<any>(response);
  }

  static async getState(id: number): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/states/${id}`, {
    });
    return this.handleResponse<any>(response);
  }

  static async createState(data: {
    id_pais?: number | null;
    name: string;
    abbreviation?: string | null;
  }): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/states`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return this.handleResponse<any>(response);
  }

  static async updateState(id: number, data: {
    id_pais?: number | null;
    name: string;
    abbreviation?: string | null;
  }): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/states/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return this.handleResponse<any>(response);
  }

  static async deleteState(id: number): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/states/${id}`, {
      method: 'DELETE',
    });
    return this.handleResponse<any>(response);
  }

  // Verification Status CRUD API methods (for verification_status table)
  static async getVerificationStatusRecords(
    cursor?: number | null,
    limit?: number,
    sort?: string,
    direction?: 'ASC' | 'DESC',
    refresh?: boolean
  ): Promise<any> {
    const searchParams = new URLSearchParams();
    
    if (cursor) searchParams.append('cursor', cursor.toString());
    if (limit) searchParams.append('limit', limit.toString());
    if (sort) searchParams.append('sort', sort);
    if (direction) searchParams.append('direction', direction);
    if (refresh) searchParams.append('refresh', 'true');

    const url = `${API_BASE_URL}/verification-status?${searchParams.toString()}`;
    const response = await fetchWithAuth(url);
    return this.handleResponse<any>(response);
  }

  static async searchVerificationStatusRecords(request: any & { refresh?: boolean }): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/verification-status/search`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return this.handleResponse<any>(response);
  }

  static async createVerificationStatusRecord(data: {
    name: string;
    status?: number;
    observations?: string | null;
  }): Promise<any> {
    const payload: Record<string, any> = {
      name: data.name,
    };
    
    if (data.status !== undefined) {
      payload.status = data.status;
    }
    
    if (data.observations !== undefined) {
      payload.observations = data.observations;
    }
    
    const response = await fetchWithAuth(`${API_BASE_URL}/verification-status`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.handleResponse<any>(response);
  }

  static async updateVerificationStatusRecord(id: number, data: {
    name?: string;
    status?: number;
    observations?: string | null;
  }): Promise<any> {
    const payload: Record<string, any> = {};
    
    if (data.name !== undefined) {
      payload.name = data.name;
    }
    
    if (data.status !== undefined) {
      payload.status = data.status;
    }
    
    if (data.observations !== undefined) {
      payload.observations = data.observations;
    }
    
    const response = await fetchWithAuth(`${API_BASE_URL}/verification-status/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.handleResponse<any>(response);
  }

  static async deleteVerificationStatusRecord(id: number): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/verification-status/${id}`, {
      method: 'DELETE',
    });
    return this.handleResponse<any>(response);
  }

  // Logs API methods
  static async getLogs(
    cursor?: number | null,
    limit?: number,
    sort?: string,
    direction?: 'ASC' | 'DESC'
  ): Promise<any> {
    const searchParams = new URLSearchParams();
    
    if (cursor) {
      searchParams.append('cursor', cursor.toString());
    }
    if (limit) {
      searchParams.append('limit', limit.toString());
    }
    if (sort) {
      searchParams.append('sort', sort);
    }
    if (direction) {
      searchParams.append('direction', direction);
    }

    const url = `${API_BASE_URL}/logs${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await fetchWithAuth(url);
    return this.handleResponse<any>(response);
  }

  static async searchLogs(request: {
    filters: any[];
    cursor?: number | null;
    limit?: number;
    sort?: string;
    direction?: 'ASC' | 'DESC';
  }): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/logs/search`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return this.handleResponse<any>(response);
  }

  // Config API methods
  static async getDatabaseConfig(): Promise<Record<string, string | number | boolean>> {
    const response = await fetchWithAuth(`${API_BASE_URL}/config/database`, {
    });
    return this.handleResponse<Record<string, string | number | boolean>>(response);
  }

  static async getHealth(): Promise<{ status: string; message: string }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/health`, {
    });
    return this.handleResponse<{ status: string; message: string }>(response);
  }

  // Statistics API methods
  static async getActiveTagsStatistics(refresh?: boolean): Promise<{ active_tags: any[] }> {
    const url = refresh 
      ? `${API_BASE_URL}/statistics/active-tags?refresh=true`
      : `${API_BASE_URL}/statistics/active-tags`;
    const response = await fetchWithAuth(url);
    return this.handleResponse<{ active_tags: any[] }>(response);
  }

  static async getTagStatistics(refresh?: boolean): Promise<{ tag_stats: any[] }> {
    const url = refresh 
      ? `${API_BASE_URL}/statistics/tag-stats?refresh=true`
      : `${API_BASE_URL}/statistics/tag-stats`;
    const response = await fetchWithAuth(url);
    return this.handleResponse<{ tag_stats: any[] }>(response);
  }

  static async getBirdsEyeView(refresh?: boolean): Promise<{ birds_eye_view: any }> {
    const url = refresh 
      ? `${API_BASE_URL}/statistics/birds-eye-view?refresh=true`
      : `${API_BASE_URL}/statistics/birds-eye-view`;
    const response = await fetchWithAuth(url);
    return this.handleResponse<{ birds_eye_view: any }>(response);
  }

  // Outreach API methods
  static async checkDomains(file: File): Promise<any> {
    // Upload CSV file directly using FormData
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/check-domains`, {
      method: 'POST',
      body: formData,
      // Don't set Content-Type header - let browser set it with boundary for FormData
    });
    return this.handleResponse<any>(response);
  }

  static async getSelectedDomains(sessionId: string): Promise<any> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/staging/${sessionId}/final`, {
    });
    return this.handleResponse<any>(response);
  }

  static async getOutreachIndustryTags(): Promise<{ industryTags: { id: number; name: string }[] }> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/industry-tags`, {
    });
    return this.handleResponse<{ industryTags: { id: number; name: string }[] }>(response);
  }

  static async updateDomainLabel(sessionId: string, domainId: number, label: string): Promise<any> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/staging/${sessionId}/label`, {
      method: 'PUT',
      body: JSON.stringify({ domainId, label }),
    });
    return this.handleResponse<any>(response);
  }

  static async updateDomainIndustryTags(sessionId: string, domainId: number, industryTags: number[]): Promise<any> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/staging/${sessionId}/industry-tags`, {
      method: 'PUT',
      body: JSON.stringify({ domainId, industryTags }),
    });
    return this.handleResponse<any>(response);
  }

  static async updateDomainType(sessionId: string, domainId: number, type: string): Promise<any> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/staging/${sessionId}/type`, {
      method: 'PUT',
      body: JSON.stringify({ domainId, type }),
    });
    return this.handleResponse<any>(response);
  }

  static async getStagingData(sessionId: string): Promise<any> {
    const url = `${OUTREACH_API_BASE_URL}/staging/${sessionId}`;
    const response = await fetchWithAuth(url, {
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response:', errorText);
      try {
        const error = JSON.parse(errorText);
        throw new Error(error.message || error.error || 'API request failed');
      } catch (e) {
        throw new Error(errorText || `HTTP ${response.status}: ${response.statusText}`);
      }
    }
    const data = await response.json();
    return data;
  }

  static async updateDomainSelection(sessionId: string, domainIds: number[], selected: boolean): Promise<any> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/staging/${sessionId}/select`, {
      method: 'PUT',
      body: JSON.stringify({ domainIds, isSelected: selected }),
    });
    return this.handleResponse<any>(response);
  }

  static async getEnrichedData(sessionId: string): Promise<any[]> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/enrich`, {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
    return this.handleResponse<any[]>(response);
  }

  static async enrichSingleData(sessionId: string, domainId: number): Promise<{
    success: boolean;
    website: string;
    preCount: number;
    postCount: number;
    newCount: number;
    insertedCount: number;
  }> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/enrich-single`, {
      method: 'POST',
      body: JSON.stringify({ sessionId, domainId }),
    });
    return this.handleResponse<any>(response);
  }


  static async getValidationData(sessionId: string, resumedDomainIds?: string): Promise<any[]> {
    const url = resumedDomainIds 
      ? `${OUTREACH_API_BASE_URL}/validation/${sessionId}?resumedDomainIds=${encodeURIComponent(resumedDomainIds)}`
      : `${OUTREACH_API_BASE_URL}/validation/${sessionId}`;
    const response = await fetchWithAuth(url, {
    });
    return this.handleResponse<any[]>(response);
  }

  // Check Reoon account balance
  static async checkAccountBalance(): Promise<{
    api_status: string;
    remaining_daily_credits: number;
    remaining_instant_credits: number;
    status: string;
  }> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/check-account-balance`, {
    });
    return this.handleResponse(response);
  }

  static async getValidationStats(): Promise<{
    creditsUsedToday: number;
    dailyLimit: number;
    remaining: number;
    today: string;
  }> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/validation-stats`);
    return this.handleResponse(response);
  }

  static async getMailWhizTemplates(): Promise<any> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/mailwhiz/templates`);
    return this.handleResponse(response);
  }

  static async sendMailWhizIntro(emails: string[], templateId: string, sessionId?: string): Promise<any> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/mailwhiz/send-intro`, {
      method: 'POST',
      body: JSON.stringify({ emails, templateId, sessionId })
    });
    return this.handleResponse(response);
  }

  static async syncMailWhizList(emails: string[], listId: string, sessionId?: string): Promise<any> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/mailwhiz/sync-list`, {
      method: 'POST',
      body: JSON.stringify({ emails, listId, sessionId })
    });
    return this.handleResponse(response);
  }

  // Validate emails using Reoon Bulk API (creates task, returns task_id)
  static async validateEmails(emails: string[], sessionId?: string): Promise<{
    status: string;
    task_id: number;
    count_processing: number;
    count_duplicates_removed: number;
    count_rejected: number;
    count_submitted: number;
    message: string;
  }> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/validate-emails`, {
      method: 'POST',
      body: JSON.stringify({ emails, sessionId }),
    });
    return this.handleResponse(response);
  }

  // Check validation task status (poll for results)
  static async checkValidationStatus(taskId: number, sessionId?: string): Promise<any> {
    const url = sessionId 
      ? `${OUTREACH_API_BASE_URL}/check-validation-status/${taskId}?sessionId=${encodeURIComponent(sessionId)}`
      : `${OUTREACH_API_BASE_URL}/check-validation-status/${taskId}`;
    const response = await fetchWithAuth(url, {
    });
    return this.handleResponse(response);
  }

  // Schedule throttled validation
  static async scheduleValidation(data: {
    emails: string[];
    sessionId: string;
    name?: string;
    dailyLimit?: number;
  }): Promise<{ success: boolean; taskId: string; message: string }> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/schedule-validation`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  // Get scheduled tasks
  static async getScheduledTasks(sessionId?: string): Promise<any[]> {
    const url = sessionId 
      ? `${OUTREACH_API_BASE_URL}/scheduled-tasks?sessionId=${encodeURIComponent(sessionId)}`
      : `${OUTREACH_API_BASE_URL}/scheduled-tasks`;
    const response = await fetchWithAuth(url);
    return this.handleResponse(response);
  }

  // Cancel scheduled task
  static async cancelScheduledTask(taskId: string): Promise<{ success: boolean; message: string }> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/cancel-scheduled-task/${taskId}`, {
      method: 'POST',
    });
    return this.handleResponse(response);
  }

  // Get task progress
  static async getScheduledTaskProgress(taskId: string): Promise<any> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/scheduled-task/${taskId}/progress`);
    return this.handleResponse(response);
  }

  static async getEmailsByDomain(domain: string, sessionId?: string): Promise<string[]> {
    // This will be used to fetch emails for selected domains
    // We'll need to create this endpoint or use existing one
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/emails-by-domain?domain=${encodeURIComponent(domain)}${sessionId ? `&sessionId=${sessionId}` : ''}`, {
    });
    return this.handleResponse<string[]>(response);
  }

  static async saveForLater(sessionId: string, domainIds: number[], sessionName?: string, stage: string = 'process'): Promise<{ success: boolean; savedDomains: number; message: string }> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/save-for-later`, {
      method: 'POST',
      body: JSON.stringify({ sessionId, domainIds, sessionName, stage }),
    });
    return this.handleResponse(response);
  }

  static async getSavedSessions(): Promise<{
    sessions: Array<{
      id: number;
      session_id: string;
      name: string | null;
      domain_count: number;
      email_count: number;
      new_emails_count?: number;
      saved_at: string;
    }>;
  }> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/saved-sessions`, {
    });
    return this.handleResponse(response);
  }

  static async getSavedSession(sessionId: string): Promise<{
    session: any;
    domains: any[];
    emailCount: number;
  }> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/saved-session/${sessionId}`, {
    });
    return this.handleResponse(response);
  }

  static async resumeSession(sessionId: string): Promise<{
    success: boolean;
    resumedDomains: number;
    message: string;
    stage?: string;
    resumedDomainIds?: number[];
  }> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/resume-session/${sessionId}`, {
      method: 'POST',
    });
    return this.handleResponse(response);
  }

  static async deleteDomains(sessionId: string, domainIds: number[]): Promise<{
    success: boolean;
    deletedDomains: number;
    deletedEmails: number;
    message: string;
  }> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/delete-domains`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sessionId, domainIds }),
    });
    return this.handleResponse(response);
  }

  static async unsaveDomains(sessionId: string, domainIds: number[]): Promise<{
    success: boolean;
    unsavedDomains: number;
    message: string;
  }> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/unsave-domains`, {
      method: 'POST',
      body: JSON.stringify({ sessionId, domainIds }),
    });
    return this.handleResponse(response);
  }

  static async getEmailResults(sessionId: string, page: number = 1, limit: number = 100, resumedDomainIds?: string): Promise<{
    domains: Array<{
      id: number;
      domain: string;
      website: string;
      name: string;
      description: string;
      location: string;
      type_: string;
      industry_tag_name: string;
      industry_tag_two_name: string;
      industry_tag_three_name: string;
      industry_tag_four_name: string;
      deliverable: number;
      risky: number;
      unknown: number;
      undeliverable: number;
      not_verified: number;
      deliverableCount: number;
      riskyCount: number;
      undeliverableCount: number;
    }>;
    deliverableEmails: Array<{
      id: number;
      email: string;
      first_name: string;
      last_name: string;
      website: string;
      title: string;
      location?: string;
      type_?: string;
      industry_tag?: number;
      industry_tag_two?: number;
      industry_tag_three?: number;
      industry_tag_four?: number;
      industry_tag_name?: string;
      industry_tag_two_name?: string;
      industry_tag_three_name?: string;
      industry_tag_four_name?: string;
      p_tag_1?: number;
      p_tag_2?: number;
      p_tag_3?: number;
      p_tag_4?: number;
      p_tag_1_name?: string;
      p_tag_2_name?: string;
      p_tag_3_name?: string;
      p_tag_4_name?: string;
    }>;
    deliverableTotal: number;
    deliverablePage: number;
    deliverableLimit: number;
    riskyEmails: Array<{
      id: number;
      email: string;
      first_name: string;
      last_name: string;
      website: string;
      title: string;
      location?: string;
      type_?: string;
      industry_tag?: number;
      industry_tag_two?: number;
      industry_tag_three?: number;
      industry_tag_four?: number;
      industry_tag_name?: string;
      industry_tag_two_name?: string;
      industry_tag_three_name?: string;
      industry_tag_four_name?: string;
      p_tag_1?: number;
      p_tag_2?: number;
      p_tag_3?: number;
      p_tag_4?: number;
      p_tag_1_name?: string;
      p_tag_2_name?: string;
      p_tag_3_name?: string;
      p_tag_4_name?: string;
    }>;
    unknownEmails: Array<{
      id: number;
      email: string;
      first_name: string;
      last_name: string;
      website: string;
      title: string;
      location?: string;
      type_?: string;
      industry_tag?: number;
      industry_tag_two?: number;
      industry_tag_three?: number;
      industry_tag_four?: number;
      industry_tag_name?: string;
      industry_tag_two_name?: string;
      industry_tag_three_name?: string;
      industry_tag_four_name?: string;
      p_tag_1?: number;
      p_tag_2?: number;
      p_tag_3?: number;
      p_tag_4?: number;
      p_tag_1_name?: string;
      p_tag_2_name?: string;
      p_tag_3_name?: string;
      p_tag_4_name?: string;
    }>;
    undeliverableEmails: Array<{
      id: number;
      email: string;
      first_name: string;
      last_name: string;
      website: string;
      title: string;
      location?: string;
      type_?: string;
      industry_tag?: number;
      industry_tag_two?: number;
      industry_tag_three?: number;
      industry_tag_four?: number;
      industry_tag_name?: string;
      industry_tag_two_name?: string;
      industry_tag_three_name?: string;
      industry_tag_four_name?: string;
      p_tag_1?: number;
      p_tag_2?: number;
      p_tag_3?: number;
      p_tag_4?: number;
      p_tag_1_name?: string;
      p_tag_2_name?: string;
      p_tag_3_name?: string;
      p_tag_4_name?: string;
    }>;
  }> {
    let url = `${OUTREACH_API_BASE_URL}/email-results/${sessionId}?page=${page}&limit=${limit}`;
    if (resumedDomainIds) {
      url += `&resumedDomainIds=${encodeURIComponent(resumedDomainIds)}`;
    }
    const response = await fetchWithAuth(url, {
    });
    return this.handleResponse(response);
  }

  static async exportOutreachCompanies(sessionId: string, resumedDomainIds?: string): Promise<Blob> {
    let url = `${OUTREACH_API_BASE_URL}/email-results/${sessionId}/export/companies`;
    if (resumedDomainIds) {
      url += `?resumedDomainIds=${encodeURIComponent(resumedDomainIds)}`;
    }
    const response = await fetchWithAuth(url, {});
    if (!response.ok) {
      throw new Error(`Failed to export companies: ${response.statusText}`);
    }
    return response.blob();
  }

  static async exportOutreachDeliverableEmails(sessionId: string, resumedDomainIds?: string): Promise<Blob> {
    let url = `${OUTREACH_API_BASE_URL}/email-results/${sessionId}/export/deliverable`;
    if (resumedDomainIds) {
      url += `?resumedDomainIds=${encodeURIComponent(resumedDomainIds)}`;
    }
    const response = await fetchWithAuth(url, {});
    if (!response.ok) {
      throw new Error(`Failed to export deliverable emails: ${response.statusText}`);
    }
    return response.blob();
  }

  static async exportOutreachRiskyEmails(sessionId: string, resumedDomainIds?: string): Promise<Blob> {
    let url = `${OUTREACH_API_BASE_URL}/email-results/${sessionId}/export/risky`;
    if (resumedDomainIds) {
      url += `?resumedDomainIds=${encodeURIComponent(resumedDomainIds)}`;
    }
    const response = await fetchWithAuth(url, {});
    if (!response.ok) {
      throw new Error(`Failed to export risky emails: ${response.statusText}`);
    }
    return response.blob();
  }

  static async exportOutreachUndeliverableEmails(sessionId: string, resumedDomainIds?: string): Promise<Blob> {
    let url = `${OUTREACH_API_BASE_URL}/email-results/${sessionId}/export/undeliverable`;
    if (resumedDomainIds) {
      url += `?resumedDomainIds=${encodeURIComponent(resumedDomainIds)}`;
    }
    const response = await fetchWithAuth(url, {});
    if (!response.ok) {
      throw new Error(`Failed to export undeliverable emails: ${response.statusText}`);
    }
    return response.blob();
  }

  static async exportOutreachUnknownEmails(sessionId: string, resumedDomainIds?: string): Promise<Blob> {
    let url = `${OUTREACH_API_BASE_URL}/email-results/${sessionId}/export/unknown`;
    if (resumedDomainIds) {
      url += `?resumedDomainIds=${encodeURIComponent(resumedDomainIds)}`;
    }
    const response = await fetchWithAuth(url, {});
    if (!response.ok) {
      throw new Error(`Failed to export unknown emails: ${response.statusText}`);
    }
    return response.blob();
  }

  static async exportOutreachExcel(sessionId: string, resumedDomainIds?: string): Promise<Blob> {
    let url = `${OUTREACH_API_BASE_URL}/email-results/${sessionId}/export/excel`;
    if (resumedDomainIds) {
      url += `?resumedDomainIds=${encodeURIComponent(resumedDomainIds)}`;
    }
    const response = await fetchWithAuth(url);
    if (!response.ok) {
      throw new Error(`Failed to export Excel: ${response.statusText}`);
    }
    return await response.blob();
  }

  static async exportAllEmails(sessionId: string, resumedDomainIds?: string): Promise<Blob> {
    let url = `${OUTREACH_API_BASE_URL}/email-results/${sessionId}/export/all`;
    if (resumedDomainIds) {
      url += `?resumedDomainIds=${encodeURIComponent(resumedDomainIds)}`;
    }
    const response = await fetchWithAuth(url, {});
    if (!response.ok) {
      throw new Error(`Failed to export all emails: ${response.statusText}`);
    }
    return response.blob();
  }

  static async deleteSession(sessionId: string): Promise<{
    message: string;
    deletedStagingRecords: number;
    deletedEmailRecords: number;
  }> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/session/${sessionId}`, {
      method: 'DELETE',
    });
    return this.handleResponse(response);
  }

  // User Management API methods (admin only)
  static async getUsers(forOptions: boolean = false): Promise<{ success: boolean; users: any[] }> {
    const url = forOptions 
      ? `${API_BASE_URL}/auth/users?for=options`
      : `${API_BASE_URL}/auth/users`;
    const response = await fetchWithAuth(url, {
    });
    return this.handleResponse<{ success: boolean; users: any[] }>(response);
  }

  static async createUser(userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    username: string;
    role: number;
  }): Promise<{ success: boolean; message: string; user: any }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/auth/users`, {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    return this.handleResponse<{ success: boolean; message: string; user: any }>(response);
  }

  static async deleteUser(userId: number): Promise<{ success: boolean; message: string }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/auth/users/${userId}`, {
      method: 'DELETE',
    });
    return this.handleResponse<{ success: boolean; message: string }>(response);
  }

  static async setupUser2FA(userId: number): Promise<{
    success: boolean;
    secret: string;
    qrCode: string;
    manualEntryKey: string;
    message: string;
  }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/auth/users/${userId}/setup-2fa`, {
      method: 'POST',
    });
    return this.handleResponse<{
      success: boolean;
      secret: string;
      qrCode: string;
      manualEntryKey: string;
      message: string;
    }>(response);
  }

  static async enableUser2FA(userId: number, code: string): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/auth/users/${userId}/enable-2fa`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
    return this.handleResponse<{ success: boolean; message: string }>(response);
  }

  static async updateUserRole(userId: number, role: number): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/auth/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
    return this.handleResponse<{ success: boolean; message: string }>(response);
  }

  static async updateUserStatus(userId: number, status: number): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/auth/users/${userId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
    return this.handleResponse<{ success: boolean; message: string }>(response);
  }

  static async updateUserPassword(userId: number, password: string): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/auth/users/${userId}/password`, {
      method: 'PUT',
      body: JSON.stringify({ password }),
    });
    return this.handleResponse<{ success: boolean; message: string }>(response);
  }


  static async setup2FA(): Promise<{
    success: boolean;
    secret: string;
    qrCode: string;
    manualEntryKey: string;
    message: string;
  }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/auth/setup-2fa`, {
      method: 'POST',
    });
    return this.handleResponse<{
      success: boolean;
      secret: string;
      qrCode: string;
      manualEntryKey: string;
      message: string;
    }>(response);
  }

  static async enable2FA(code: string): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/auth/enable-2fa`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
    return this.handleResponse<{ success: boolean; message: string }>(response);
  }

  // Profile API methods
  static async updateProfile(profileData: {
    firstName: string;
    lastName: string;
    email: string;
    username: string;
    profileImage?: string;
  }): Promise<{
    success: boolean;
    message: string;
    user: any;
  }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/auth/profile`, {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
    return this.handleResponse<{ success: boolean; message: string; user: any }>(response);
  }

  // Import API methods
  static async uploadImportFile(
    file: File,
    type: 'company' | 'people',
    mode: 'insert' | 'update' | 'upsert'
  ): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mode', mode);
    
    const token = localStorage.getItem('token');
    if (token) {
      formData.append('token', token);
    }

    const response = await fetchWithAuth(`${API_BASE_URL}/import/${type}`, {
      method: 'POST',
      body: formData,
      // Don't set headers - let fetchWithAuth handle it correctly for FormData
    });

    const result = await this.handleResponse<{ error: boolean; message: string; jobId: string }>(response);
    return result.jobId;
  }

  static async getImportProgress(jobId: string): Promise<{
    jobId: string;
    status: 'queued' | 'processing' | 'completed' | 'failed';
    total: number;
    processed: number;
    inserted: number;
    updated: number;
    skipped: number;
    failed: number;
    errors: Array<{ row: number; error: string; data?: string }>;
    startedAt: string;
    completedAt?: string;
  }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/import/status/${jobId}`);
    const result = await this.handleResponse<{
      error: boolean;
      data: {
        jobId: string;
        status: 'queued' | 'processing' | 'completed' | 'failed';
        total: number;
        processed: number;
        inserted: number;
        updated: number;
        skipped: number;
        failed: number;
        errors: Array<{ row: number; error: string; data?: string }>;
        startedAt: string;
        completedAt?: string;
      };
    }>(response);
    return result.data;
  }

  // Campaigns API methods
  static async getCampaigns(): Promise<{ campaigns: Campaign[] }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/campaigns`);
    return this.handleResponse<{ campaigns: Campaign[] }>(response);
  }

  static async getCampaign(id: number): Promise<{ campaign: Campaign; domains: string[] }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/campaigns/${id}`);
    return this.handleResponse<{ campaign: Campaign; domains: string[] }>(response);
  }

  static async createCampaign(campaignData: {
    name: string;
    tag_filters?: FilterCriteria[] | null;
    date_filters?: any | null;
    schedule_type: 'manual' | 'weekly_mon' | 'monthly_1st' | 'quarterly';
    status?: 'active' | 'paused' | 'completed';
    domains?: string[];
  }): Promise<{ success: boolean; campaignId: number; domains_count: number; message: string }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/campaigns`, {
      method: 'POST',
      body: JSON.stringify(campaignData),
    });
    return this.handleResponse<{ success: boolean; campaignId: number; domains_count: number; message: string }>(response);
  }

  static async updateCampaign(
    id: number,
    campaignData: {
      name?: string;
      schedule_type?: 'manual' | 'weekly_mon' | 'monthly_1st' | 'quarterly';
      status?: 'active' | 'paused' | 'completed';
    }
  ): Promise<{ success: boolean; message: string }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/campaigns/${id}`, {
      method: 'PUT',
      body: JSON.stringify(campaignData),
    });
    return this.handleResponse<{ success: boolean; message: string }>(response);
  }

  static async deleteCampaign(id: number): Promise<{ success: boolean; message: string }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/campaigns/${id}`, {
      method: 'DELETE',
    });
    return this.handleResponse<{ success: boolean; message: string }>(response);
  }

  static async getLeadFinderStats(filters: FilterCriteria[]): Promise<LeadFinderStatsResponse> {
    const response = await fetchWithAuth(`${API_BASE_URL}/campaigns/lead-finder-stats`, {
      method: 'POST',
      body: JSON.stringify({ filters }),
    });
    return this.handleResponse<LeadFinderStatsResponse>(response);
  }

  static async runCampaign(id: number): Promise<{ success: boolean; sessionId: string; domainsCount: number; message: string }> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/campaigns/${id}/run`, {
      method: 'POST',
    });
    return this.handleResponse<{ success: boolean; sessionId: string; domainsCount: number; message: string }>(response);
  }

  static async getWebsitesByFilter(filters: FilterCriteria[]): Promise<{ success: boolean; count: number; websites: string[] }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/companies/websites-by-filter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ filters }),
    });
    return this.handleResponse<{ success: boolean; count: number; websites: string[] }>(response);
  }

  static async checkDomainsJson(domains: string[]): Promise<any> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/check-domains`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ domains }),
    });
    return this.handleResponse<any>(response);
  }

  static async sendToSearch(params: { filters?: FilterCriteria[], companyIds?: number[], name?: string }): Promise<{ success: boolean; count: number; list_name: string | null; message: string }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/companies/send-to-search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });
    return this.handleResponse<{ success: boolean; count: number; list_name: string | null; message: string }>(response);
  }

  static async getSearchList(list?: string): Promise<any[]> {
    const qs = list ? `?list=${encodeURIComponent(list)}` : '';
    const response = await fetchWithAuth(`${API_BASE_URL}/companies/search-list${qs}`);
    return this.handleResponse<any[]>(response);
  }

  static async getSearchListBatches(): Promise<{ list_name: string; status: string; count: number; created_at: string }[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/companies/search-list/batches`);
    return this.handleResponse(response);
  }

  static async deleteSearchListItems(ids: number[]): Promise<{ success: boolean; deleted: number }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/companies/search-list`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    return this.handleResponse(response);
  }

  static async updateSearchListBulk(items: any[]): Promise<{ success: boolean; message: string }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/companies/search-list/bulk`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
    });
    return this.handleResponse<{ success: boolean; message: string }>(response);
  }

  static async googleResults(query: string, num = 5, exclude: string[] = [], deep_search = false): Promise<{ success: boolean; results: { url: string; title: string; description: string; displayLink: string; tag: string; existsInDb: boolean }[]; allResults?: { url: string; title: string; description: string; displayLink: string; tag: string; existsInDb: boolean }[]; hasMore: boolean; deepSearchCount?: number }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/search-llm/google-results`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, num, exclude, deep_search }),
    });
    return this.handleResponse(response);
  }

  static async saveSearchResult(data: { list_name: string; source_query?: string; url: string; title?: string; description?: string; source_domain_id?: number }): Promise<{ success: boolean; already_in_db: boolean }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/search-llm/saved-results`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  static async removeSearchResult(list_name: string, url: string): Promise<{ success: boolean }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/search-llm/saved-results`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ list_name, url }),
    });
    return this.handleResponse(response);
  }

  static async getSavedResults(list?: string): Promise<any[]> {
    const qs = list ? `?list=${encodeURIComponent(list)}` : '';
    const response = await fetchWithAuth(`${API_BASE_URL}/search-llm/saved-results${qs}`);
    return this.handleResponse<any[]>(response);
  }

  static async saveDeepSearchResults(list_name: string, source_query: string, results: any[]): Promise<{ success: boolean; inserted: number }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/search-llm/deep-search-results/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ list_name, source_query, results }),
    });
    return this.handleResponse(response);
  }

  static async getDeepSearchResults(list?: string): Promise<any[]> {
    const qs = list ? `?list=${encodeURIComponent(list)}` : '';
    const response = await fetchWithAuth(`${API_BASE_URL}/search-llm/deep-search-results${qs}`);
    return this.handleResponse<any[]>(response);
  }

  static async markBatchDone(list_name: string): Promise<{ success: boolean; nextList: string | null }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/companies/search-list/mark-done?list=${encodeURIComponent(list_name)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
    });
    return this.handleResponse(response);
  }

  static async processSearchLlm(ids: number[]): Promise<{ success: boolean; results: any[] }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/search-llm/process`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ids }),
    });
    return this.handleResponse<{ success: boolean; results: any[] }>(response);
  }

  static async sendSearchToOutreach(ids: number[]): Promise<{ success: boolean; count: number; message: string; sessionId: string }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/companies/search-list/send-to-outreach`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ids }),
    });
    return this.handleResponse<{ success: boolean; count: number; message: string; sessionId: string }>(response);
  }

  static async updateSessionName(sessionId: string, name: string): Promise<{ success: boolean; message: string }> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/session/${sessionId}/name`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name }),
    });
    return this.handleResponse<{ success: boolean; message: string }>(response);
  }

  static async getRecentQueries(): Promise<{ success: boolean; queries: { id: number; date: string; filters: FilterCriteria[] }[] }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/companies/recent-queries`, {
      method: 'GET',
    });
    return this.handleResponse<{ success: boolean; queries: { id: number; date: string; filters: FilterCriteria[] }[] }>(response);
  }

  static async saveRecentQuery(filters: FilterCriteria[]): Promise<{ success: boolean; message: string; id: number }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/companies/recent-queries`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ filters }),
    });
    return this.handleResponse<{ success: boolean; message: string; id: number }>(response);
  }

  static async getOutreachApiKeys(): Promise<{ gmo: string; reoon: string; mailwhiz: string }> {
    const response = await fetchWithAuth(`${OUTREACH_API_BASE_URL}/settings/keys`);
    return this.handleResponse(response);
  }

  // --- Search Settings API Methods ---
  static async getLlmSettings(): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/search-settings/llm`);
    return this.handleResponse<any>(response);
  }

  static async updateLlmSettings(data: any): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/search-settings/llm`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return this.handleResponse<any>(response);
  }

  static async getKnowledgeBase(): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/search-settings/knowledge-base`);
    return this.handleResponse<any>(response);
  }

  static async updateKnowledgeBase(content: string): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/search-settings/knowledge-base`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    });
    return this.handleResponse<any>(response);
  }

  static async getNewsletterPresets(): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/search-settings/newsletters`);
    return this.handleResponse<any>(response);
  }

  static async updateNewsletterPresets(presets: any[]): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/search-settings/newsletters`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ presets }),
    });
    return this.handleResponse<any>(response);
  }
}

